import React, { useState, useEffect } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import { Settings, Bell, Globe, Moon, Save, RefreshCw } from 'lucide-react';
import './SettingsPage.css';

function SettingsPage() {
  const { showSuccess, showError } = useNotification();
  const [settings, setSettings] = useState({
    language: 'de',
    notifications: {
      newEvents: true,
      weatherAlerts: true,
      driverMessages: true,
      systemUpdates: false
    },
    theme: 'light',
    autoRefresh: true,
    refreshInterval: 30, // seconds
    mapProvider: 'google',
    distanceUnit: 'km'
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Try to load from backend API first
      const token = localStorage.getItem('token');
      if (token) {
        const response = await fetch('http://localhost:3002/api/auth/settings', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSettings({ ...settings, ...data.data.settings });
          return;
        }
      }
      
      // Fallback to localStorage
      const savedSettings = localStorage.getItem('userSettings');
      if (savedSettings) {
        setSettings({ ...settings, ...JSON.parse(savedSettings) });
      }
    } catch (error) {
      console.error('Settings load error:', error);
      // Fallback to localStorage
      const savedSettings = localStorage.getItem('userSettings');
      if (savedSettings) {
        setSettings({ ...settings, ...JSON.parse(savedSettings) });
      }
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Try to save to backend API first
      const token = localStorage.getItem('token');
      if (token) {
        const response = await fetch('http://localhost:3002/api/auth/settings', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(settings)
        });

        if (response.ok) {
          setLastSaved(new Date());
          showSuccess('Einstellungen erfolgreich gespeichert!', {
            title: 'Gespeichert'
          });
          // Also save to localStorage as backup
          localStorage.setItem('userSettings', JSON.stringify(settings));
          return;
        } else {
          // If API fails, fall back to localStorage
          console.warn('API save failed, using localStorage');
        }
      }
      
      // Fallback to localStorage
      localStorage.setItem('userSettings', JSON.stringify(settings));
      setLastSaved(new Date());
      
      showSuccess('Einstellungen lokal gespeichert (Server nicht verfügbar)', {
        title: 'Lokal gespeichert'
      });
      
    } catch (error) {
      console.error('Settings save error:', error);
      // Fallback to localStorage
      localStorage.setItem('userSettings', JSON.stringify(settings));
      setLastSaved(new Date());
      
      showError('Server nicht erreichbar - Einstellungen lokal gespeichert', {
        title: 'Offline gespeichert'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (category, key, value) => {
    if (category) {
      setSettings(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [key]: value
        }
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  const resetToDefaults = () => {
    setSettings({
      language: 'de',
      notifications: {
        newEvents: true,
        weatherAlerts: true,
        driverMessages: true,
        systemUpdates: false
      },
      theme: 'light',
      autoRefresh: true,
      refreshInterval: 30,
      mapProvider: 'google',
      distanceUnit: 'km'
    });
    showSuccess('Einstellungen auf Standard zurückgesetzt', {
      title: 'Zurückgesetzt'
    });
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Einstellungen werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="settings-title">
          <Settings className="h-6 w-6 text-blue-600" />
          <h1>Einstellungen</h1>
        </div>
        
        <div className="settings-actions">
          <button 
            onClick={resetToDefaults}
            className="btn-reset"
          >
            <RefreshCw className="h-4 w-4" />
            Zurücksetzen
          </button>
          <button 
            onClick={saveSettings}
            disabled={saving}
            className="btn-save"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>

      {lastSaved && (
        <div className="save-status">
          ✅ Zuletzt gespeichert: {lastSaved.toLocaleTimeString('de-DE')}
        </div>
      )}

      <div className="settings-content">
        {/* General Settings */}
        <div className="settings-section">
          <h2 className="section-title">
            <Globe className="h-5 w-5" />
            Allgemeine Einstellungen
          </h2>
          
          <div className="settings-grid">
            <div className="setting-item">
              <label className="setting-label">
                Sprache
                <span className="setting-description">Wähle deine bevorzugte Sprache</span>
              </label>
              <select 
                value={settings.language}
                onChange={(e) => handleSettingChange(null, 'language', e.target.value)}
                className="setting-select"
              >
                <option value="de">Deutsch</option>
                <option value="en">English</option>
                <option value="tr">Türkçe</option>
              </select>
            </div>

            <div className="setting-item">
              <label className="setting-label">
                Theme
                <span className="setting-description">Hell oder dunkel</span>
              </label>
              <select 
                value={settings.theme}
                onChange={(e) => handleSettingChange(null, 'theme', e.target.value)}
                className="setting-select"
              >
                <option value="light">Hell</option>
                <option value="dark">Dunkel</option>
                <option value="auto">Automatisch</option>
              </select>
            </div>

            <div className="setting-item">
              <label className="setting-label">
                Entfernungseinheit
                <span className="setting-description">Kilometer oder Meilen</span>
              </label>
              <select 
                value={settings.distanceUnit}
                onChange={(e) => handleSettingChange(null, 'distanceUnit', e.target.value)}
                className="setting-select"
              >
                <option value="km">Kilometer</option>
                <option value="mi">Meilen</option>
              </select>
            </div>

            <div className="setting-item">
              <label className="setting-label">
                Kartenanbieter
                <span className="setting-description">Bevorzugter Kartenservice</span>
              </label>
              <select 
                value={settings.mapProvider}
                onChange={(e) => handleSettingChange(null, 'mapProvider', e.target.value)}
                className="setting-select"
              >
                <option value="google">Google Maps</option>
                <option value="openstreet">OpenStreetMap</option>
                <option value="mapbox">Mapbox</option>
              </select>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="settings-section">
          <h2 className="section-title">
            <Bell className="h-5 w-5" />
            Benachrichtigungen
          </h2>
          
          <div className="settings-grid">
            <div className="setting-item toggle-item">
              <label className="setting-label">
                Neue Events
                <span className="setting-description">Benachrichtigung bei neuen Veranstaltungen</span>
              </label>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.notifications.newEvents}
                  onChange={(e) => handleSettingChange('notifications', 'newEvents', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item toggle-item">
              <label className="setting-label">
                Wetter-Alarme
                <span className="setting-description">Warnungen bei schlechtem Wetter</span>
              </label>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.notifications.weatherAlerts}
                  onChange={(e) => handleSettingChange('notifications', 'weatherAlerts', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item toggle-item">
              <label className="setting-label">
                Fahrer Nachrichten
                <span className="setting-description">Chat-Nachrichten von anderen Fahrern</span>
              </label>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.notifications.driverMessages}
                  onChange={(e) => handleSettingChange('notifications', 'driverMessages', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item toggle-item">
              <label className="setting-label">
                System Updates
                <span className="setting-description">Benachrichtigungen über App-Updates</span>
              </label>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.notifications.systemUpdates}
                  onChange={(e) => handleSettingChange('notifications', 'systemUpdates', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Data & Refresh Settings */}
        <div className="settings-section">
          <h2 className="section-title">
            <RefreshCw className="h-5 w-5" />
            Daten & Aktualisierung
          </h2>
          
          <div className="settings-grid">
            <div className="setting-item toggle-item">
              <label className="setting-label">
                Automatische Aktualisierung
                <span className="setting-description">Dashboard automatisch aktualisieren</span>
              </label>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.autoRefresh}
                  onChange={(e) => handleSettingChange(null, 'autoRefresh', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {settings.autoRefresh && (
              <div className="setting-item">
                <label className="setting-label">
                  Aktualisierungsintervall
                  <span className="setting-description">Wie oft sollen Daten aktualisiert werden</span>
                </label>
                <select 
                  value={settings.refreshInterval}
                  onChange={(e) => handleSettingChange(null, 'refreshInterval', parseInt(e.target.value))}
                  className="setting-select"
                >
                  <option value={15}>15 Sekunden</option>
                  <option value={30}>30 Sekunden</option>
                  <option value={60}>1 Minute</option>
                  <option value={300}>5 Minuten</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Account Settings */}
        <div className="settings-section danger-section">
          <h2 className="section-title">
            ⚠️ Konto Einstellungen
          </h2>
          
          <div className="danger-actions">
            <button className="btn-danger">
              Passwort ändern
            </button>
            <button className="btn-danger">
              Konto löschen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
